import {
	AccountStatusEnum,
	AccountStatusLabel,
	AssignmentFranchiseTypeEnum,
	CaroEnum,
	CONTENT_PROCESS_INFO_OPTION,
	DIVISION_RECOMMENDER_OPTIONS,
	MidTermDepartureEnum,
	PageTypeEnum,
	ReEntryRecommendationStatusEnum,
	TEMPLATE_APPLICANT_FIELDS,
} from '@/constant';
import {
	GetWorkerApplicantAssignmentListResponse,
	GetWorkerApplicantDetailResponse,
	GetWorkerApplicantListResponse,
	GetWorkerApplicantProcessInfoListResponse,
	GetWorkerApplicantProgramListResponse,
	TApplicantCustomField,
	TApplicantLodging,
	TApplicantRecommender,
	TUploadExcelApplicantFailedItem,
	TUploadExcelApplicantItem,
	TUploadExcelApplicantResponse,
	UploadExcelUserInfo,
} from '@/services';

import { DefaultOptionType } from 'antd/es/select';
import {
	convertExtensionPeriodToName,
	convertFilePath,
	convertGenderCodeToName,
	convertInfrastructureCodeToName,
	convertNationCodeToName,
	convertReEntryRecommendationStatus,
	convertResidenceTypeCodeToName,
	convertVisaCodeToName,
	formatDate,
	formatVisaTime,
	generateErrorMessagesUploadExcel,
	getDiffExpirationDateString,
	map,
	parseOtherValue,
	showDataGridWithSymbol,
} from 'utils';

export const formatWorkerApplicantDetail = (
	input: GetWorkerApplicantDetailResponse
) => {
	const dataCustomField = input?.customField?.reduce((field, curr) => {
		(field as any)[`customField${curr.fieldNo}`] = curr.value;
		return field;
	}, {}) as TApplicantCustomField;

	const midTermDeparture = parseOtherValue(
		input?.midTermDeparture,
		MidTermDepartureEnum.OTHER
	);

	return {
		...dataCustomField,
		...input,
		nationalName: convertNationCodeToName(input?.nationalCode),
		reEntryRecommendationStatus:
			convertReEntryRecommendationStatus(input?.reentryRecommendationStatus) ||
			'',
		genderName: convertGenderCodeToName(input?.gender),
		registDt: formatDate(input?.registDt) || '',
		updateDt: formatDate(input?.updateDt) || '',
		avatarFile:
			convertFilePath(input?.avatarFile || input?.avatar?.[0]?.path) || '',
		avatarId: input?.avatarId || input?.avatar?.[0]?.id,
		extensionPeriodName: convertExtensionPeriodToName(input?.extensionPeriod),
		expirationDate: formatDate(input?.expirationDate) || '',
		midTermDeparture: midTermDeparture?.hasOther
			? MidTermDepartureEnum.OTHER
			: input?.midTermDeparture || '',
		midTermDepartureOther: midTermDeparture.text || '',
		midTermDepartureFull: input?.midTermDeparture || '',
	};
};

export const formatWorkerApplicantListItem = (
	input: GetWorkerApplicantListResponse['content'][number]
) => {
	return {
		...input,
		id: input?.applicantId,
		applicantBirthDate: formatDate(input?.applicantBirthDate),
		applicantPassportIssueDate:
			formatDate(input?.applicantPassportIssueDate) || null,
		applicantPassportExpireDate:
			formatDate(input?.applicantPassportExpireDate) || null,
		applicantEntryDate: formatDate(input?.applicantEntryDate) || null,
		applicantChangeWorkDate: formatDate(input?.applicantChangeWorkDate) || null,
		applicantUnauthorizedDepartureDate:
			formatDate(input?.applicantUnauthorizedDepartureDate) || null,
		applicantDepartureDate: formatDate(input?.applicantDepartureDate) || null,
		applicantExtensionStayDate:
			`${formatDate(input?.applicantExtensionStayDate)}${
				input?.applicantExtensionPeriod
					? ` (${convertExtensionPeriodToName(input?.applicantExtensionPeriod)})`
					: ''
			}` || null,
		applicantExpirationDate: formatDate(input?.applicantExpirationDate) || null,
		applicantRegistDt: formatDate(input?.applicantRegistDt),
		applicantUpdateDt: formatDate(input?.applicantUpdateDt),
		applicantReentryRecommendationStatus: convertReEntryRecommendationStatus(
			input?.applicantReentryRecommendationStatus
		) as ReEntryRecommendationStatusEnum,
		applicantIsPublic: !!input?.applicantIsPublic,
		applicantIsReEntry: !!input?.applicantIsReEntry,
		applicantAgreementDt: formatDate(input?.applicantAgreementDt) || null,
		applicantMidTermDepartureOther: parseOtherValue(
			input?.applicantMidTermDeparture,
			MidTermDepartureEnum.OTHER
		).text,
	};
};

export const formatWorkerExcelApplicantListItem = (
	input: GetWorkerApplicantListResponse['content'][number]
) => {
	return {
		...formatWorkerApplicantListItem(input),
		applicantDepartureDate:
			getDiffExpirationDateString(input?.applicantDepartureDate || '') || '',
		applicantIsWithdrawn:
			AccountStatusLabel[
				Number(input?.applicantIsWithdrawn) as AccountStatusEnum
			] || '',
		applicantAgreementDt: formatDate(input?.applicantAgreementDt) || '',
	};
};

export const formatWorkerApplicantList = (
	input: GetWorkerApplicantListResponse['content']
) => {
	return map(input, formatWorkerApplicantListItem);
};

export const formatWorkerExcelApplicantList = (
	input: GetWorkerApplicantListResponse['content']
) => {
	return map(input, formatWorkerExcelApplicantListItem);
};

export const formatWorkerApplicantAssignmentDetail = (
	data: GetWorkerApplicantAssignmentListResponse['content'][number]
) => {
	return {
		...data,
		assignmentTime: showDataGridWithSymbol(
			formatDate(data?.workStartDate),
			formatDate(data?.workEndDate),
			'~'
		),
		typeName:
			data?.type === AssignmentFranchiseTypeEnum.PUBLIC
				? '공공형'
				: '일반(농가)',
	};
};

export const formatWorkerApplicantAssignmentList = (
	input: GetWorkerApplicantAssignmentListResponse['content']
) => {
	return map(input, formatWorkerApplicantAssignmentDetail);
};

export const formatWorkerApplicantProgramDetail = (
	data: GetWorkerApplicantProgramListResponse['content'][number]
) => {
	return {
		...data,
		birthDate: formatDate(data?.birthDate),
		startDate: formatDate(data?.startDate),
		endDate: formatDate(data?.endDate),
		entryDate: formatDate(data?.entryDate),
		extensionStayDate: formatDate(data?.extensionStayDate),
		expirationDate: formatDate(data?.expirationDate),
		departureDate: formatDate(data?.departureDate),
		changeWorkDate: formatDate(data?.changeWorkDate),
		unauthorizedDepartureDate: formatDate(data?.unauthorizedDepartureDate),
		reentryRecommendationStatus:
			convertReEntryRecommendationStatus(data?.reentryRecommendationStatus) ||
			'',
		isPublic: data?.isPublic ? CaroEnum.O : CaroEnum.X,
		isReEntry: data?.isReEntry ? CaroEnum.O : CaroEnum.X,
		visaCode: convertVisaCodeToName(data?.visaCode),
		visaTime: formatVisaTime(data?.visaTime),
		extensionPeriodName: convertExtensionPeriodToName(data?.extensionPeriod),
	};
};

export const formatWorkerApplicantProgramList = (
	input: GetWorkerApplicantProgramListResponse['content']
) => {
	return map(input, formatWorkerApplicantProgramDetail);
};

export const formatWorkerApplicantProcessInfoDetail = (
	data: GetWorkerApplicantProcessInfoListResponse['content'][number]
) => {
	return {
		...data,
		id: data?.referId,
		processingDate: formatDate(data?.processingDate),
		registDt: formatDate(data?.registDt),
		typeName:
			CONTENT_PROCESS_INFO_OPTION.find((item) => item.value === data?.type)
				?.label || '',
	};
};

export const formatWorkerApplicantProcessInfoList = (
	input: GetWorkerApplicantProcessInfoListResponse['content']
) => {
	return map(input, formatWorkerApplicantProcessInfoDetail);
};
export const formatUploadExcelApplicantFailedItem = (
	input: TUploadExcelApplicantFailedItem,
	customFields: DefaultOptionType[],
	userInfo: UploadExcelUserInfo
) => {
	// Map custom fields to the desired structure
	const formattedCustomFields = customFields.map((field) => ({
		label: String(field.label),
		value: field.value as keyof TUploadExcelApplicantItem,
	}));

	// Define field label mappings
	const fieldLabelMappings: Record<string, string> = {
		publicFranchiseName: '공공형 사업자(농협)',
		email: '아이디(이메일)',
		// Add more field mappings here as needed
	};

	// Update template fields with custom labels
	const modifiedTemplateFields = TEMPLATE_APPLICANT_FIELDS.map((field) =>
		fieldLabelMappings[field.value]
			? { ...field, label: fieldLabelMappings[field.value] }
			: field
	);

	// Combine modified template fields with formatted custom fields
	const allFields = [...modifiedTemplateFields, ...formattedCustomFields];

	// Generate error messages
	const reasonFailure = generateErrorMessagesUploadExcel({
		response: { errorList: input.errorList },
		templateFields: allFields,
		pageType: PageTypeEnum.APPLICANT,
		userInfo,
	});
	return {
		...input.data,
		reasonFailure,
	};
};

export const formatUploadExcelApplicantFailedList = ({
	input,
	customFields,
	userInfo,
}: {
	input: TUploadExcelApplicantResponse;
	customFields: DefaultOptionType[];
	userInfo: UploadExcelUserInfo;
}) => {
	return map(input, (item) =>
		formatUploadExcelApplicantFailedItem(item, customFields, userInfo)
	);
};

export const formatWorkerApplicantRecommender = (
	data: TApplicantRecommender
) => {
	if (!data) return {} as TApplicantRecommender;
	return {
		...data,
		birthDate: formatDate(data?.birthDate),
		typeName: DIVISION_RECOMMENDER_OPTIONS.find(
			(item) => item.value === data?.type
		)?.label,
		providerNationalName: convertNationCodeToName(data?.providerNationalCode),
		visaName: convertResidenceTypeCodeToName(data?.visaCode),
	};
};

export const formatWorkerApplicantLodging = (
	data: TApplicantLodging,
	id: number
) => {
	if (!data) return {} as TApplicantLodging;
	return {
		...data,
		id,
		facilityTypeName: convertInfrastructureCodeToName(data?.facilityType),
	};
};
